import { CellContext, ColumnDef, EllipsisTextWithTooltip, Checkbox } from '@shape-ds/components';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { t } from 'i18next';
import Status from 'components/Status/Status';
import { dayjs } from 'lib/dayjs';
import SapNoteStatus from 'components/SapNotesStatus/SapNoteStatus';
import LinkComponent from 'pages/Asset/tabs/Monitoring/components/Tables/components/Link/Link';
import TooltipInfo from 'pages/Asset/tabs/Monitoring/components/TooltipInfo/TooltipInfo';
import {
  AlarmHistory,
  MaintenancePlan,
  SapNote,
  VibrationData,
} from 'queries/externalReferences/contract';
import RiskStatus from 'components/RiskStatus/RiskStatus';
import { TableData } from './Filters/Filters';

const getIsMaintenancePlain = (row: MaintenancePlan | SapNote): row is SapNote => {
  return !!(row as MaintenancePlan).children;
};

export const dateValidation = (date: string | null) => {
  return date
    ? dayjs(date).format(t('components:date_filter.date.datetime_minute'))
    : t('components:inputs.invalid_date');
};

export const maintenanceStatusMap: MaintenancePlanStatusMap = {
  late: 'error',
  open: 'warning',
  closed: 'success',
};
export const vibrationStatusMap: VibrationStatusMap = {
  Critical: 'error',
  Normal: 'neutral',
  Observation: 'warning',
  Alert: 'warning',
};
export const sapColumns: { [key: string]: Partial<ColumnDef<SapNote>> } = {
  status: {
    enableSorting: false,
    size: 30,
    cell: (cell: CellContext<SapNote, unknown>) => {
      const row = cell.row.original;
      return <SapNoteStatus status={row.status} showCode={false} />;
    },
  },
  description: {
    enableSorting: false,
    cell: (cell: CellContext<SapNote, unknown>) => {
      return <EllipsisTextWithTooltip text={cell.row.original.description || ''} />;
    },
  },
  notification_text: { size: 80 },
  start_date: { size: 80 },
  end_date: { size: 80 },
  type: { size: 60 },
  priority: { size: 65 },
  link: {
    size: 30,
    enableSorting: false,
    cell: (cell: CellContext<SapNote, unknown>) => {
      const row = cell.row.original;

      return <LinkComponent link={row.link} />;
    },
  },
};
export const maintenanceColumns: { [key: string]: Partial<ColumnDef<MaintenancePlan>> } = {
  // Aguardando desenvolvimento dados/back
  // status: {
  //   size: 30,
  //   header: '',
  //   enableSorting: false,
  //   cell: (cell: CellContext<MaintenancePlan, unknown>) => {
  //     const row = cell.row.original as MaintenancePlan | SapNote;
  //     const isMaintenancePlain = getIsMaintenancePlain(row);

  //     // if (isMaintenancePlain) {
  //     return (
  //       <Status
  //         status={maintenanceStatusMap[row.status as MaintenancePlanStatus]}
  //         variant="calendar"
  //       />
  //     );
  //     // }

  //     // return <SapNoteStatus status={row.status} />;
  //   },
  // },
  description: { enableSorting: false },

  item_id: { size: 90 },
  group: { size: 80 },
  group_counter: { size: 105 },
};
export const buildDateColumns = <T extends TableData>(
  dinamicColumns: string[],
  dateFormat?: string,
) => {
  return dinamicColumns.map((key) => ({
    id: key,
    enableSorting: false,
    size: 80,
    header: dayjs(key).format(dateFormat ?? t('formats:date.lts')),

    cell: (cell: CellContext<T, unknown>) => {
      const row = cell.row.original;
      const collumnValue = dateFormat ? row[dayjs(key).format('MMM/YYYY')] : row[key];

      if (collumnValue === null || collumnValue === undefined) {
        return (
          <Status status="neutral" variant="text">
            {String(t('monitoring:tables.vibrationAnalysisStatus.noData'))}
          </Status>
        );
      }
      if (typeof collumnValue === 'string' || typeof collumnValue === 'number') {
        return collumnValue;
      }
      if (collumnValue !== null) {
        const { status, observation } = collumnValue as VibrationData;
        return (
          <Status
            status={status ? vibrationStatusMap[status as keyof VibrationStatusMap] : 'Normal'}
            variant="text"
          >
            {String(t(`monitoring:tables.vibrationAnalysisStatus.${status}`))}
            {observation && status !== 'Normal' && <TooltipInfo>{observation}</TooltipInfo>}
          </Status>
        );
      }

      return t('monitoring:tables.vibrationAnalysisStatus.noData');
    },
  }));
};

// Extended version with checkbox support for Evidence tables
export const buildDateColumnsWithCheckbox = <T extends TableData>(
  dinamicColumns: string[],
  options: {
    dateFormat?: string;
    checkboxConfig?: {
      isCellSelected: (row: T, key: string) => boolean;
      handleCellSelectionChange: (params: {
        description: string;
        columnKey: string;
        value: boolean;
      }) => void;
      getDescription: (row: T) => string;
    };
  } = {},
) => {
  const { dateFormat, checkboxConfig } = options;

  return dinamicColumns.map((key) => ({
    id: key,
    enableSorting: false,
    size: checkboxConfig ? 105 : 80,
    header: dateFormat ? dayjs(key).format(dateFormat) : key,

    cell: (cell: CellContext<T, unknown>) => {
      const row = cell.row?.original || {};
      const formattedKey = dateFormat ? dayjs(key).format('MMM/YYYY') : key;
      const collumnValue =
        typeof row === 'object' && row !== null
          ? formattedKey in row
            ? row[formattedKey]
            : undefined
          : undefined;

      const renderStatusContent = () => {
        if (collumnValue === null || collumnValue === undefined) {
          return (
            <Status status="neutral" variant="text">
              {String(t('monitoring:tables.vibrationAnalysisStatus.noData'))}
            </Status>
          );
        }
        if (typeof collumnValue === 'string' || typeof collumnValue === 'number') {
          return collumnValue;
        }
        if (collumnValue !== null && typeof collumnValue === 'object' && 'status' in collumnValue) {
          const { status, observation } = collumnValue as VibrationData;
          return (
            <Status
              status={status ? vibrationStatusMap[status as keyof VibrationStatusMap] : 'neutral'}
              variant="text"
            >
              {String(t(`monitoring:tables.vibrationAnalysisStatus.${status}`))}
              {observation && status !== 'Normal' && <TooltipInfo>{observation}</TooltipInfo>}
            </Status>
          );
        }
        return collumnValue || t('monitoring:tables.vibrationAnalysisStatus.noData');
      };

      if (!checkboxConfig) {
        return renderStatusContent();
      }

      const { isCellSelected, handleCellSelectionChange, getDescription } = checkboxConfig;

      const description = row && typeof getDescription === 'function' ? getDescription(row) : '';

      const isValidDescription = typeof description === 'string' && description.trim() !== '';

      const isSelected =
        row && key && typeof isCellSelected === 'function' ? isCellSelected(row, key) : false;

      const handleCellToggle = (value: boolean) => {
        if (isValidDescription && key && typeof handleCellSelectionChange === 'function') {
          handleCellSelectionChange({ description, columnKey: key, value });
        }
      };

      return (
        <div
          className={`oil-analysis-cell ${isSelected ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (row && key && isValidDescription) {
              handleCellToggle(!isSelected);
            }
          }}
        >
          <Checkbox
            className={`oil-analysis-checkbox ${isSelected ? 'selected' : ''}`}
            checked={Boolean(isSelected)}
            onChange={(e) => {
              e.stopPropagation();
              // Only toggle if we have valid event, row, key and description
              if (e && e.target && row && key && isValidDescription) {
                handleCellToggle(e.target.checked);
              }
            }}
          />
          <div className="oil-analysis-cell-value">{renderStatusContent()}</div>
        </div>
      );
    },
  }));
};

export const alarmHistoryColumns: { [key: string]: Partial<ColumnDef<AlarmHistory>> } = {
  severity: {
    enableSorting: false,
    size: 50,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      return <RiskStatus risk={row.severity} />;
    },
  },
  description: {
    enableSorting: false,
    size: 111,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      return <EllipsisTextWithTooltip text={row.description} />;
    },
  },
  start_date: {
    enableSorting: true,
    size: 120,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      const dateValidated = dateValidation(row.start_date);

      return (
        <p
          className={`${
            dateValidated === t('components:inputs.invalid_date') ? 'status-no-data' : ''
          }`}
        >
          {dateValidated}
        </p>
      );
    },
  },
  end_date: {
    enableSorting: true,
    size: 120,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      const dateValidated = dateValidation(row.end_date);
      return (
        <p
          className={`${
            dateValidated === t('components:inputs.invalid_date') ? 'status-no-data' : ''
          }`}
        >
          {dateValidated}
        </p>
      );
    },
  },
  status: {
    enableSorting: false,
    size: 100,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      const status = row.status ? row.status.toLowerCase() : t('components:noData');

      return (
        <div className={`alarm-history-status status-${status}`}>
          {(status === 'new' || status === 'open') && (
            <NotificationsActiveOutlined sx={{ fontSize: 24, paddingRight: 1 }} />
          )}
          {status === 'acknowledged' ||
            (status === 'closed' && (
              <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 24, paddingRight: 1 }} />
            ))}
          <p className={`${row.status ? '' : 'status-no-data'}`}>{status}</p>
        </div>
      );
    },
  },
  classification: {
    enableSorting: false,
    size: 120,
    cell: (cell: CellContext<AlarmHistory, unknown>) => {
      const row = cell.row.original;
      const classification = row.classification
        ? row.classification.toLowerCase()
        : t('monitoring:tables.columns.ALARM_HISTORY.noData');
      return <span className={`classification ${classification}`}>{classification}</span>;
    },
  },
};
